import { useEffect, useState } from "react";
import { Modal, Input, Select, Button, Popconfirm, Switch, ConfigProvider } from "antd";
import { DeleteOutlined, EditOutlined, FileTextOutlined, PlusOutlined } from "@ant-design/icons";
import toast from "react-hot-toast";
import { get, post } from "../../../../services/ApiService";

const { Option } = Select;

const THEME = {
  primary: "#4e7661",
  light: "#eef4f1",
  border: "#c5d9ce",
  muted: "#3d5f4e",
};

const APPROVE_REASONS = ["Valid Order", "Other"];
const DECLINE_REASONS = [
  "Buyer Cancelled",
  "Over Medication Limit",
  "Potential Overuse / Abuse",
  "Incomplete Assessment",
  "Contraindication",
  "Invalid Rx",
  "Other",
];

const reasonsForStatus = (status) =>
  status === "cancel" ? DECLINE_REASONS : APPROVE_REASONS;

const fieldLabel = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.7px",
  color: "#aaa",
  margin: "0 0 6px",
};

export const VALID_ORDER_NOTE =
  "Relevant clinical, safety and supply criteria reviewed; no concerns identified and supply deemed appropriate.";

const FALLBACK_TEMPLATES = [
  {
    _id: "valid-order",
    name: "Valid Order",
    note: VALID_ORDER_NOTE,
    isDefault: true,
  },
];

const sameName = (left, right) =>
  String(left || "").trim().toLowerCase() === String(right || "").trim().toLowerCase();

export const noteForSelectedReason = (title, note, indicator, templates = []) => {
  const list = Array.isArray(templates) ? templates : [];
  const matches = list.filter((template) => sameName(template.name, title));
  const templateNotes = new Set(list.map((template) => template.note).filter(Boolean));
  const defaultMatch = matches.find((template) => template.isDefault);

  if (title === "Valid Order" && indicator === "Red") {
    return "";
  }

  if (matches.some((template) => template.note === note)) {
    return note;
  }

  if (defaultMatch) {
    return defaultMatch.note;
  }

  if (matches.length === 1) {
    return matches[0].note;
  }

  if (templateNotes.has(note)) {
    return "";
  }

  return note || "";
};

const ReasonNoteModal = ({
  open,
  onOk,
  onCancel,
  status,
  reasonTitle,
  note,
  onReasonChange,
  onNoteChange,
  onTemplatesChange,
}) => {
  const [templates, setTemplates] = useState(FALLBACK_TEMPLATES);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [editor, setEditor] = useState(null);
  const [editorName, setEditorName] = useState("");
  const [editorNote, setEditorNote] = useState("");
  const [editorIsDefault, setEditorIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!open) {
      setEditor(null);
      return undefined;
    }

    let cancelled = false;
    const loadTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const res = await get("api/orders/reason-note-templates");
        if (!cancelled && res?.success && Array.isArray(res.templates)) {
          setTemplates(res.templates);
          onTemplatesChange?.(res.templates);
        } else if (!cancelled) {
          onTemplatesChange?.(FALLBACK_TEMPLATES);
        }
      } catch (error) {
        if (!cancelled) onTemplatesChange?.(FALLBACK_TEMPLATES);
        if (!cancelled && !error.handledByInterceptor) {
          toast.error("Templates could not be loaded");
        }
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    };

    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [open, onTemplatesChange]);

  const otherDefaultFor = (name, excludeId) =>
    templates.some(
      (template) =>
        sameName(template.name, name) &&
        template.isDefault &&
        String(template._id) !== String(excludeId || "")
    );

  const openEditor = (template) => {
    setEditor(template || { _id: null });
    setEditorName(template?.name || "");
    setEditorNote(template?.note || "");
    setEditorIsDefault(Boolean(template?.isDefault));
  };

  const changeEditorCondition = (value) => {
    setEditorName(value);
    const editingThisDefault = editor?.isDefault && sameName(editor?.name, value);
    setEditorIsDefault(editingThisDefault || !otherDefaultFor(value, editor?._id));
  };

  const closeEditor = () => {
    if (saving) return;
    setEditor(null);
  };

  const deleteTemplate = async (template) => {
    if (!template?._id || template._id === "valid-order") return;
    setDeletingId(template._id);
    try {
      const res = await post(`api/orders/reason-note-templates/delete/${template._id}`);
      if (res?.success && Array.isArray(res.templates)) {
        setTemplates(res.templates);
        onTemplatesChange?.(res.templates);
        toast.success("Template deleted");
      } else {
        toast.error(res?.error || "Template could not be deleted");
      }
    } catch (error) {
      if (!error.handledByInterceptor) {
        toast.error(error?.response?.data?.error || "Template could not be deleted");
      }
    } finally {
      setDeletingId(null);
    }
  };

  const saveTemplate = async () => {
    const name = editorName.trim();
    const templateNote = editorNote.trim();
    const allowedConditions = [...new Set([...APPROVE_REASONS, ...DECLINE_REASONS])];
    if (!name || !allowedConditions.includes(name)) {
      toast.error("Please select a condition");
      return;
    }
    if (!templateNote) {
      toast.error("Template note is required");
      return;
    }

    const isEdit = Boolean(editor?._id && editor._id !== "valid-order");
    const defaultLocked = otherDefaultFor(name, editor?._id);
    const isDefault = defaultLocked ? false : editorIsDefault;
    if (isEdit && editor?.isDefault && !sameName(editor.name, name)) {
      const oldOthers = templates.filter(
        (template) =>
          sameName(template.name, editor.name) &&
          String(template._id) !== String(editor._id)
      );
      if (oldOthers.length > 0 && !oldOthers.some((template) => template.isDefault)) {
        toast.error(
          `Set another template as the default for ${editor.name} before changing the condition`
        );
        return;
      }
    }

    setSaving(true);
    try {
      const res = await post(
        isEdit
          ? `api/orders/reason-note-templates/update/${editor._id}`
          : "api/orders/reason-note-templates/add",
        { name, note: templateNote, isDefault }
      );
      if (res?.success && Array.isArray(res.templates)) {
        setTemplates(res.templates);
        onTemplatesChange?.(res.templates);
        setEditor(null);
        toast.success(isEdit ? "Template updated" : "Template added");
      } else {
        toast.error(res?.error || "Template could not be saved");
      }
    } catch (error) {
      if (!error.handledByInterceptor) {
        toast.error(error?.response?.data?.error || "Template could not be saved");
      }
    } finally {
      setSaving(false);
    }
  };

  const visibleTemplates = templates
    .filter(
      (template) =>
        reasonsForStatus(status).some((reason) => sameName(reason, template.name)) &&
        (!reasonTitle || sameName(template.name, reasonTitle))
    )
    .slice()
    .sort((left, right) => Number(Boolean(right.isDefault)) - Number(Boolean(left.isDefault)));

  const defaultLocked = otherDefaultFor(editorName, editor?._id);

  return (
    <>
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: THEME.light,
                border: `1px solid ${THEME.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FileTextOutlined style={{ color: THEME.primary, fontSize: 16 }} />
            </span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
                Please provide reason
              </div>
            </div>
          </div>
        }
        open={open}
        onCancel={onCancel}
        footer={null}
        width={820}
        destroyOnClose
      >
        <div
          style={{
            display: "flex",
            gap: 20,
            alignItems: "stretch",
            padding: "4px 0 0",
          }}
        >
          <div style={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column" }}>
            <div style={{ marginBottom: 12 }}>
              <p style={fieldLabel}>Reason</p>
              <Select
                placeholder="Please select"
                value={reasonTitle || undefined}
                onChange={onReasonChange}
                style={{ width: "100%" }}
              >
                {reasonsForStatus(status).map((reason) => (
                  <Option key={reason} value={reason}>
                    {reason}
                  </Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 12, flex: 1 }}>
              <p style={fieldLabel}>Note</p>
              <Input.TextArea
                rows={6}
                value={note || ""}
                onChange={(event) => onNoteChange(event.target.value)}
                placeholder="Enter the reason note..."
              />
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <Button
                block
                size="large"
                onClick={onCancel}
                style={{ height: 40, fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                block
                size="large"
                onClick={onOk}
                style={{
                  background: THEME.primary,
                  borderColor: THEME.primary,
                  fontWeight: 600,
                  height: 40,
                }}
              >
                Submit
              </Button>
            </div>
          </div>

          <div
            style={{
              flex: "0 0 320px",
              borderLeft: "1px solid #eee",
              paddingLeft: 16,
              display: "flex",
              flexDirection: "column",
              minHeight: 280,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <p style={{ ...fieldLabel, margin: 0 }}>Templates</p>
              <button
                type="button"
                title="Add template"
                aria-label="Add template"
                onClick={() => openEditor(null)}
                style={{
                  border: 0,
                  background: "transparent",
                  color: THEME.primary,
                  fontSize: 14,
                  cursor: "pointer",
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                <PlusOutlined />
              </button>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                flex: 1,
                maxHeight: 320,
                overflowY: "auto",
                paddingRight: 2,
              }}
            >
              {loadingTemplates && visibleTemplates.length === 0 && (
                <div style={{ fontSize: 12, color: "#888" }}>Loading templates...</div>
              )}
              {!loadingTemplates && visibleTemplates.length === 0 && (
                <div style={{ fontSize: 12, color: "#888" }}>
                  No templates for this condition.
                </div>
              )}
              {visibleTemplates.map((template) => {
                const selected =
                  note === template.note && sameName(template.name, reasonTitle);
                const canEdit = template._id && template._id !== "valid-order";
                return (
                  <div
                    key={template._id}
                    onClick={() => onNoteChange(template.note)}
                    style={{
                      textAlign: "left",
                      border: selected
                        ? `2px solid ${THEME.primary}`
                        : "1px solid #eee",
                      borderRadius: 10,
                      padding: "8px 10px",
                      background: selected ? THEME.light : "#fafafa",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 8,
                        marginBottom: 6,
                      }}
                    >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            minWidth: 0,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.7px",
                              color: selected ? THEME.primary : "#aaa",
                            }}
                          >
                            {template.name}
                          </div>
                          {template.isDefault && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: "0.4px",
                                textTransform: "uppercase",
                                color: THEME.primary,
                                background: "#fff",
                                border: `1px solid ${THEME.border}`,
                                borderRadius: 999,
                                padding: "1px 6px",
                                lineHeight: 1.4,
                              }}
                            >
                              Default
                            </span>
                          )}
                        </div>
                      {canEdit && (
                        <span style={{ display: "inline-flex", gap: 8, flexShrink: 0 }}>
                          <button
                            type="button"
                            title="Edit template"
                            aria-label="Edit template"
                            onClick={(event) => {
                              event.stopPropagation();
                              openEditor(template);
                            }}
                            style={{
                              border: 0,
                              background: "transparent",
                              color: THEME.muted,
                              fontSize: 13,
                              cursor: "pointer",
                              padding: 0,
                              lineHeight: 1,
                            }}
                          >
                            <EditOutlined />
                          </button>
                          <Popconfirm
                            title="Delete this template?"
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => deleteTemplate(template)}
                          >
                            <button
                              type="button"
                              title="Delete template"
                              aria-label="Delete template"
                              disabled={deletingId === template._id}
                              onClick={(event) => event.stopPropagation()}
                              style={{
                                border: 0,
                                background: "transparent",
                                color: "#cf1322",
                                fontSize: 13,
                                cursor: "pointer",
                                padding: 0,
                                lineHeight: 1,
                              }}
                            >
                              <DeleteOutlined />
                            </button>
                          </Popconfirm>
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        lineHeight: 1.4,
                        color: selected ? THEME.muted : "#666",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {template.note}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: THEME.light,
                border: `1px solid ${THEME.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FileTextOutlined style={{ color: THEME.primary, fontSize: 16 }} />
            </span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
                {editor?._id && editor._id !== "valid-order"
                  ? "Edit template"
                  : "Add template"}
              </div>
              <div style={{ fontSize: 11, color: "#999", fontWeight: 400 }}>
                This note can be selected when approving or declining
              </div>
            </div>
          </div>
        }
        open={Boolean(editor)}
        onCancel={closeEditor}
        footer={null}
        width={480}
        maskClosable={!saving}
        closable={!saving}
        destroyOnClose
        zIndex={1100}
      >
        <div style={{ padding: "4px 0 8px" }}>
          <div style={{ marginBottom: 16 }}>
            <p style={fieldLabel}>Condition</p>
            <Select
              size="large"
              placeholder="Please select"
              value={editorName || undefined}
              onChange={changeEditorCondition}
              style={{ width: "100%" }}
            >
              {(editorName && !reasonsForStatus(status).includes(editorName)
                ? [editorName, ...reasonsForStatus(status)]
                : reasonsForStatus(status)
              ).map((reason) => (
                <Option key={reason} value={reason}>
                  {reason}
                </Option>
              ))}
            </Select>
          </div>
          <div style={{ marginBottom: 16 }}>
            <p style={fieldLabel}>Note</p>
            <Input.TextArea
              rows={5}
              value={editorNote}
              onChange={(event) => setEditorNote(event.target.value)}
              placeholder="Enter the template note..."
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 16,
              padding: "10px 12px",
              borderRadius: 10,
              border: `1px solid ${defaultLocked ? "#e6e6e6" : THEME.border}`,
              background: defaultLocked ? "#fafafa" : THEME.light,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: defaultLocked ? "#999" : THEME.muted,
                }}
              >
                Default for this reason
              </div>
              {defaultLocked && (
                <div style={{ fontSize: 12, color: "#888", marginTop: 4, lineHeight: 1.4 }}>
                  Another template is already set as the default for this reason.
                </div>
              )}
            </div>
            <ConfigProvider theme={{ token: { colorPrimary: THEME.primary } }}>
              <Switch
                checked={defaultLocked ? false : editorIsDefault}
                disabled={defaultLocked || saving}
                onChange={setEditorIsDefault}
              />
            </ConfigProvider>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Button
              block
              size="large"
              disabled={saving}
              onClick={closeEditor}
              style={{ height: 44, fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              block
              size="large"
              loading={saving}
              onClick={saveTemplate}
              style={{
                background: THEME.primary,
                borderColor: THEME.primary,
                fontWeight: 600,
                height: 44,
              }}
            >
              Save template
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ReasonNoteModal;
